import * as React from 'react';
import { isDefined, noop } from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { getSlope, getYIntercept } from '@react-financial-charts/interactive/lib/components/InteractiveStraightLine';
import { isHoverForInteractiveType, saveNodeType, terminate } from 'react-financial-charts';
import { EachTriangle } from './Wrapper';
import { clampYToChart, ShapeType } from '@/index';
import { toTriangleRFCProps } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';
import { chartOrder } from '@/utils/chartConfig';
import type { FibChannelProps } from './FibChannel';
import type { TriangleDragData } from './Wrapper/EachFibChannel';
import type { DragOverrideBase, RenderNode, MoreProps, TriangleRenderNode } from '../../../types/commontypes';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';

/** 드래그 중 임시 상태. uuid + TriangleDragData. */
interface TriangleDragOverride extends DragOverrideBase, TriangleDragData {}

interface TriangleState {
  channels?: RenderNode | RenderNode[] | null;
  override?: TriangleDragOverride;
}

export class Triangle extends React.Component<FibChannelProps, TriangleState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: any = null;
  static get defaultProps() {
    if (!Triangle._cachedDefaultProps) {
      Triangle._cachedDefaultProps = {
        onStart: noop,
        onSelect: noop,
        currentPositionStroke: '#000000',
        currentPositionstrokeOpacity: 1,
        currentPositionStrokeWidth: 3,
        currentPositionRadius: 0,
        shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select object',
          selectedText: '',
        },
        channels: [],
      };
    }
    return Triangle._cachedDefaultProps;
  }

  private terminate: () => void;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;

  private getSelectionState: (...args: unknown[]) => unknown;
  private mouseMoved = false;

  public constructor(props: FibChannelProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState = isHoverForInteractiveType('triangles').bind(this);

    this.state = {};
  }

  public render() {
    const {
      channels,
      currentPositionRadius = Triangle.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      enabled,
      hoverText,
      onDoubleClick,
      sharedState,
      chartId,
      isAnyShapeEnabled,
    } = this.props;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const activeNode = getActiveTemplateNode(ShapeType.TRIANGLE) as TriangleRenderNode;
    const appearance = toTriangleRFCProps(activeNode);

    const { override } = this.state;

    const overrideUuid = isDefined(override) ? override.uuid : null;

    const tempCondition =
      sharedState?.type === ShapeType.TRIANGLE &&
      chartId === sharedState?.chartId &&
      sharedState?.endXY &&
      sharedState?.status !== 'completed';
    const tempChannel = tempCondition ? (
      <EachTriangle
        interactive={false}
        uuid={''}
        startXY={sharedState.startXY ?? []}
        endXY={sharedState.endXY ?? []}
        dy={sharedState.dy ?? 0}
        endXY2={sharedState.endXY2}
        appearance={appearance}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        hoverText={hoverText as React.ComponentProps<typeof EachTriangle>['hoverText']}
        selected={true}
        stroke={appearance.strokeStyle}
        fill={appearance.fillStyle}
      />
    ) : null;

    return (
      <g>
        {channels.map((each, idx) => {
          if (each.hidden) return null;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const eachAppearance = toTriangleRFCProps(each as TriangleRenderNode);
          const eachAppearanceAny = eachAppearance as Record<string, unknown>;
          const mergedEachProps = each.uuid === overrideUuid ? { ...each, ...override } : each;
          return (
            <EachTriangle
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              {...(mergedEachProps as unknown as React.ComponentProps<typeof EachTriangle>)}
              selected={(each as unknown as { selected?: boolean }).selected}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              hoverText={hoverText as React.ComponentProps<typeof EachTriangle>['hoverText']}
              appearance={eachAppearance}
              onDrag={this.handleDragChannel}
              onDragComplete={this.handleDragChannelComplete}
              onSelect={this.handleSelect}
              onDragStart={(uuid: string) => this.setState({ override: { uuid } })}
              uuid={each.uuid}
              onHover={this.handleHover}
              locked={each.locked}
              stroke={eachAppearance.strokeStyle}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              fill={eachAppearanceAny.fillStyle as string}
              onDoubleClick={onDoubleClick}
              chartId={chartId}
              sharedState={sharedState}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        {tempChannel}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          // onClick={this.handleEnd}
          onMouseMove={this.handleDrawChannel}
        />
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | number | null, moreProps?: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { channels, onSelect } = this.props;

    const newtriangles = channels.map(channel => ({
      ...channel,
      selected: channel.uuid === selectedUuid,
    }));

    if (onSelect) {
      onSelect(e, newtriangles, moreProps);
    }
  };

  private readonly handleDragChannel = (_: React.MouseEvent, uuid: string, newXYValue: TriangleDragData) => {
    const { override } = this.state;
    const { hoverId } = this.props;
    if (override) {
      if (override.uuid !== uuid || override.uuid !== hoverId) {
        return;
      }
    }
    this.setState({
      override: {
        uuid,
        ...newXYValue,
      },
    });
  };

  private readonly handleDragChannelComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { override } = this.state;
    const { channels, onDragComplete } = this.props;
    const startXY = override?.startXY;
    const endXY = override?.endXY;
    const endXY2 = override?.endXY2;
    if (!isDefined(startXY) || !isDefined(endXY2) || !isDefined(endXY)) {
      return;
    }
    if (isDefined(override)) {
      const { uuid, ...rest } = override;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const newtriangles = channels.map(each =>
        each.uuid === uuid
          ? {
              ...each,
              ...rest,
              selected: true,
              updatedAt: Date.now(),
            }
          : {
              ...each,
              selected: false,
            },
      ) as RenderNode[];
      onDragComplete?.(e, newtriangles, moreProps);
      this.setState({ override: undefined });
    }
  };

  private readonly handleStart = (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const xy = xyValue as [number, number];
    const { sharedState, enabled } = this.props;
    if (!enabled) return;
    if (sharedState?.status !== 'started') {
      this.mouseMoved = false;
      const { onStart } = this.props;
      if (onStart !== undefined) {
        onStart(e, xy, moreProps);
      }
    } else {
      const shapesChartId = sharedState?.chartId;
      // Only do min/max clamp if sharedState exists
      const yValue = clampYToChart(moreProps.chartId, shapesChartId, moreProps.chartConfigs, chartOrder, xy[1]);
      this.handleEnd(e, [xy[0], yValue], moreProps);
    }
  };

  private readonly handleEnd = (e: React.MouseEvent, _xyValue: [number, number], moreProps: MoreProps) => {
    const { channels, chartId, sharedState, setSharedState } = this.props;

    if (this.mouseMoved && isDefined(sharedState) && isDefined(sharedState.startXY)) {
      if (!isDefined(sharedState.dy)) {
        setSharedState?.({
          ...sharedState,
          dy: 0,
        });
      } else {
        const targetChartId = sharedState?.chartId || chartId;
        const shapeUuid = crypto.randomUUID();
        const lastProps = getActiveTemplateNode(ShapeType.TRIANGLE);
        const defaults = defaultShapePropertyValues[ShapeType.TRIANGLE];
        const initial = { ...defaults, ...lastProps };
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const newTriangles = [
          ...channels.map(d => ({ ...d, selected: false })),
          {
            ...initial,
            ...sharedState,
            selected: true,
            type: ShapeType.TRIANGLE,
            chartId: targetChartId,
            uuid: shapeUuid,
            hidden: false,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          },
        ] as RenderNode[];

        this.setState(
          {
            channels: newTriangles,
          },
          () => {
            const { onComplete } = this.props;
            if (onComplete !== undefined) {
              onComplete(e, newTriangles, moreProps);
            }
          },
        );
      }
    }
  };

  private readonly handleDrawChannel = (_: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    const { sharedState, setSharedState, chartId } = this.props;

    if (isDefined(sharedState) && isDefined(sharedState.startXY)) {
      this.mouseMoved = true;
      const startChartId = sharedState.chartId;
      this.mouseMoved = true;
      const pointerChartId = moreProps.currentCharts?.[0];

      // Only draw for shapes created on this chart
      if (chartId !== startChartId) return;

      const [x] = xyValue;
      let [, y] = xyValue;

      // Clamp Y if pointer is on a different chart
      y = clampYToChart(pointerChartId, startChartId, moreProps.chartConfigs, chartOrder, y);
      if (!isDefined(sharedState?.dy)) {
        setSharedState?.({
          ...sharedState,
          endXY: [x, y],
        });
      } else {
        const m = getSlope(sharedState.startXY, sharedState.endXY);
        const b = getYIntercept(m, sharedState.endXY);
        // @ts-expect-error

        const y1Value = m * sharedState.startXY[0] + b;
        const dy = y - y1Value;
        setSharedState?.({
          ...sharedState,
          endXY2: [x, y],
          dy: dy,
        });
      }
    }
  };
}
